<script setup lang="ts">
import {
  computed,
  getCurrentInstance,
  nextTick,
  ref,
  useSlots,
  watchEffect,
  type VNode,
} from "vue";

const properties = defineProps<{
  /** Optional two-way binding for active tab */
  modelValue?: string;
  /** Initial tab when not using v-model */
  defaultTab?: string;
  /** When true, the tab group fills its container and tab content scrolls */
  fillContainer?: boolean;
}>();

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "update:modelValue": [value: string];
}>();

const slots = useSlots();

interface TabMeta {
  name: string;
  label: string;
}

const tabs = computed<TabMeta[]>(() => {
  const defaultSlot = slots.default?.();
  if (!defaultSlot) return [];
  return defaultSlot
    .filter((vnode: VNode) => vnode.props?.name)
    .map((vnode: VNode) => ({
      name: vnode.props?.name as string,
      label: (vnode.props?.label as string) || (vnode.props?.name as string),
    }));
});

const internalActive = ref(
  properties.modelValue ?? properties.defaultTab ?? ""
);
const tabButtons = ref<(HTMLButtonElement | undefined)[]>([]);
const tabGroupId = `tab-group-${String(getCurrentInstance()?.uid ?? "default")}`;

watchEffect(() => {
  if (tabs.value.length === 0) {
    throw new Error("TabGroup requires at least one Tab child component.");
  }
});

const activeTab = computed({
  get: () => {
    const candidate = properties.modelValue ?? internalActive.value;
    if (tabs.value.some((tab) => tab.name === candidate)) {
      return candidate;
    }
    return tabs.value[0]?.name ?? "";
  },
  set: (value: string) => {
    internalActive.value = value;
    emit("update:modelValue", value);
  },
});

function getVNodeName(vnode: VNode): string | undefined {
  return vnode.props?.name as string | undefined;
}

function getTabId(tabName: string): string {
  return `${tabGroupId}-tab-${tabName}`;
}

function getTabPanelId(tabName: string): string {
  return `${tabGroupId}-panel-${tabName}`;
}

function setTabButtonRef(element: unknown, index: number): void {
  if (element instanceof HTMLButtonElement) {
    tabButtons.value[index] = element;
    return;
  }

  if (typeof element === "object" && element !== null && "$el" in element) {
    const rootElement = (element as { $el?: unknown }).$el;
    if (rootElement instanceof HTMLButtonElement) {
      tabButtons.value[index] = rootElement;
    }
  }
}

async function activateTabByIndex(index: number): Promise<void> {
  const nextTab = tabs.value[index];
  if (!nextTab) return;
  activeTab.value = nextTab.name;
  await nextTick();
  tabButtons.value[index]?.focus();
}

async function onTabKeydown(
  event: KeyboardEvent,
  index: number
): Promise<void> {
  if (tabs.value.length < 2) return;

  const lastIndex = tabs.value.length - 1;
  let nextIndex: number | undefined;

  switch (event.key) {
    case "ArrowRight": {
      nextIndex = index === lastIndex ? 0 : index + 1;
      break;
    }
    case "ArrowLeft": {
      nextIndex = index === 0 ? lastIndex : index - 1;
      break;
    }
    case "Home": {
      nextIndex = 0;
      break;
    }
    case "End": {
      nextIndex = lastIndex;
      break;
    }
    default: {
      return;
    }
  }

  event.preventDefault();
  await activateTabByIndex(nextIndex);
}
</script>

<template>
  <div :class="{ 'flex h-full min-h-0 flex-1 flex-col': fillContainer }">
    <div
      class="tab-group-list shrink-0"
      role="tablist"
      aria-orientation="horizontal"
    >
      <button
        v-for="(tab, index) in tabs"
        :id="getTabId(tab.name)"
        :key="tab.name"
        :ref="(element) => setTabButtonRef(element, index)"
        class="tab-group-button"
        :class="
          activeTab === tab.name
            ? 'tab-group-button--active'
            : 'tab-group-button--inactive'
        "
        :aria-controls="getTabPanelId(tab.name)"
        :aria-selected="activeTab === tab.name"
        :tabindex="activeTab === tab.name ? 0 : -1"
        role="tab"
        @click="activeTab = tab.name"
        @keydown="onTabKeydown($event, index)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div
      :class="
        fillContainer
          ? 'flex h-full min-h-0 flex-1 flex-col overflow-y-auto'
          : ''
      "
    >
      <div
        v-if="activeTab"
        :id="getTabPanelId(activeTab)"
        class="flex h-full min-h-0 flex-1 flex-col"
        :aria-labelledby="getTabId(activeTab)"
        role="tabpanel"
      >
        <template
          v-for="vnode in $slots.default?.()"
          :key="getVNodeName(vnode)"
        >
          <component :is="vnode" v-if="getVNodeName(vnode) === activeTab" />
        </template>
      </div>
    </div>
  </div>
</template>
